import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CustomerdatabaseService } from './customerdatabase.service';

export type WorkspaceTabType = 'new' | 'home' | 'active-sourcing' | 'run-ads' | 'agent-chat' | 'workflow' | 'ars' | 'onboarding' | 'profile' | 'job-offers';

export interface WorkspaceTab {
  id: string;
  type: WorkspaceTabType;
  title: string;
  status: 'idle' | 'running' | 'completed' | 'error';
  pinned?: boolean;
  createdAt: string;
  data?: {
    sessionId?: string;
    agentId?: string;
    agentName?: string;
    searchQuery?: string;
    autoExecute?: boolean;
    agentMode?: 'auto' | 'manual';
    searchMethod?: string;
    searchId?: string;
    campaignId?: string;
    campaignName?: string;
    agenticMode?: boolean;
    maxInterviews?: number;
    view?: string;
  };
}

const STORAGE_KEY = 'workspace_tabs';
const ACTIVE_TAB_KEY = 'workspace_active_tab';
const CLOSED_TABS_KEY = 'workspace_closed_tabs';
const MAX_CLOSED_TABS = 20;

const TAB_TYPE_TITLES: Record<WorkspaceTabType, string> = {
  'new': 'New Tab',
  'home': 'Home',
  'active-sourcing': 'Active Sourcing',
  'run-ads': 'Run Ads',
  'agent-chat': 'Agent Chat',
  'workflow': 'Workflows',
  'ars': 'ARS',
  'onboarding': 'Getting Started',
  'profile': 'My Profile',
  'job-offers': 'Externe Stellen'
};

export const PINNED_HOME_ID = 'pinned_home';
export const PINNED_SECONDARY_ID = 'pinned_secondary';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceTabService {
  private tabs$ = new BehaviorSubject<WorkspaceTab[]>([]);
  private activeTabId$ = new BehaviorSubject<string>('');
  private closedTabs$ = new BehaviorSubject<WorkspaceTab[]>([]);
  private firestoreLoaded = false;
  private persistTimer: any = null;
  private loadingFromFirestore = false;

  constructor(private customerDB: CustomerdatabaseService) {
    this.loadFromLocalStorage();
    this.loadClosedTabsFromLocalStorage();
    this.customerDB.isAuthenticatedOnCustDb$.subscribe(isAuth => {
      if (isAuth && !this.firestoreLoaded) {
        this.loadFromFirestore();
      } else if (!isAuth) {
        this.firestoreLoaded = false;
      }
    });
  }

  getTabs(): Observable<WorkspaceTab[]> {
    return this.tabs$.asObservable();
  }

  getActiveTabId(): Observable<string> {
    return this.activeTabId$.asObservable();
  }

  getActiveTab(): WorkspaceTab | null {
    const id = this.activeTabId$.value;
    return this.tabs$.value.find(t => t.id === id) || null;
  }

  getTabsSnapshot(): WorkspaceTab[] {
    return this.tabs$.value;
  }

  getClosedTabs(): Observable<WorkspaceTab[]> {
    return this.closedTabs$.asObservable();
  }

  async loadFromFirestore(): Promise<void> {
    if (this.firestoreLoaded || this.loadingFromFirestore) return;
    this.loadingFromFirestore = true;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    try {
      const saved = await this.customerDB.loadWorkspaceTabs();
      const savedTabs = saved ? this.withoutPlaceholderTabs(saved.tabs) : [];
      if (savedTabs.length > 0) {
        this.tabs$.next(savedTabs);
        if (saved!.activeTabId && savedTabs.some(t => t.id === saved!.activeTabId)) {
          this.activeTabId$.next(saved!.activeTabId);
        } else {
          this.activeTabId$.next(savedTabs[0].id);
        }
        this.persistLocal();
        console.log('[WorkspaceTabService] Loaded', savedTabs.length, 'tabs from Firestore');
        if (savedTabs.length !== saved!.tabs.length) {
          this.persistToFirestoreDebounced();
        }
      }
      this.firestoreLoaded = true;
      if (savedTabs.length === 0) {
        const currentTabs = this.tabs$.value;
        if (currentTabs.length > 0) {
          console.log('[WorkspaceTabService] No Firestore data, seeding with', currentTabs.length, 'local tabs');
          this.persistToFirestoreDebounced();
        }
      }
    } catch (e) {
      console.error('[WorkspaceTabService] Failed to load from Firestore:', e);
      this.firestoreLoaded = true;
    } finally {
      this.loadingFromFirestore = false;
    }
  }

  createTab(type?: WorkspaceTabType, data?: any, activate: boolean = true): WorkspaceTab {
    const tab: WorkspaceTab = {
      id: this.generateId(),
      type: type || 'new',
      title: TAB_TYPE_TITLES[type || 'new'] || 'New Tab',
      status: 'idle',
      createdAt: new Date().toISOString(),
      data: data || {}
    };
    const tabs = [...this.tabs$.value, tab];
    this.tabs$.next(tabs);
    if (activate) {
      this.activeTabId$.next(tab.id);
    }
    this.persist();
    return tab;
  }

  isTabPinned(id: string): boolean {
    const tab = this.tabs$.value.find(t => t.id === id);
    return !!tab?.pinned;
  }

  closeTab(id: string): void {
    const closingTab = this.tabs$.value.find(t => t.id === id);
    if (closingTab?.pinned) return;
    const tabs = this.tabs$.value.filter(t => t.id !== id);
    if (closingTab && closingTab.type !== 'new') {
      const closed = [closingTab, ...this.closedTabs$.value].slice(0, MAX_CLOSED_TABS);
      this.closedTabs$.next(closed);
      this.persistClosedTabs();
    }
    if (tabs.length === 0) {
      this.createTab();
      return;
    }
    this.tabs$.next(tabs);
    if (this.activeTabId$.value === id) {
      this.activeTabId$.next(tabs[tabs.length - 1].id);
    }
    this.persist();
  }

  restoreTab(id: string): void {
    const tab = this.closedTabs$.value.find(t => t.id === id);
    if (!tab) return;
    this.closedTabs$.next(this.closedTabs$.value.filter(t => t.id !== id));
    this.persistClosedTabs();
    const tabs = [...this.tabs$.value, tab];
    this.tabs$.next(tabs);
    this.activeTabId$.next(tab.id);
    this.persist();
  }

  clearClosedTabs(): void {
    this.closedTabs$.next([]);
    this.persistClosedTabs();
  }

  setActiveTab(id: string): void {
    if (this.tabs$.value.some(t => t.id === id)) {
      this.activeTabId$.next(id);
      this.persist();
    }
  }

  updateTab(id: string, changes: Partial<WorkspaceTab>): void {
    const tabs = this.tabs$.value.map(t => {
      if (t.id !== id) return t;
      if (t.pinned && changes.title) {
        const { title, ...rest } = changes;
        if (Object.keys(rest).length === 0) return t;
        return { ...t, ...rest };
      }
      const updated = { ...t, ...changes };
      if (changes.type && !changes.title) {
        updated.title = TAB_TYPE_TITLES[changes.type] || changes.type;
      }
      return updated;
    });
    this.tabs$.next(tabs);
    this.persist();
  }

  setTabStatus(id: string, status: WorkspaceTab['status']): void {
    this.updateTab(id, { status });
  }

  ensurePinnedTabs(onboardingComplete: boolean): void {
    let tabs = [...this.tabs$.value];
    let changed = false;

    const hasHome = tabs.some(t => t.id === PINNED_HOME_ID);
    if (!hasHome) {
      const homeTab: WorkspaceTab = {
        id: PINNED_HOME_ID,
        type: 'home',
        title: 'Home',
        status: 'idle',
        pinned: true,
        createdAt: new Date().toISOString(),
        data: {}
      };
      tabs.unshift(homeTab);
      changed = true;
    } else {
      const homeTab = tabs.find(t => t.id === PINNED_HOME_ID);
      if (homeTab && (homeTab.title !== 'Home' || !homeTab.pinned)) {
        homeTab.title = 'Home';
        homeTab.pinned = true;
        changed = true;
      }
    }

    const expectedType: WorkspaceTabType = onboardingComplete ? 'profile' : 'onboarding';
    const expectedTitle = onboardingComplete ? 'My Profile' : 'Getting Started';

    const hasSecondary = tabs.some(t => t.id === PINNED_SECONDARY_ID);
    if (!hasSecondary) {
      const secondaryTab: WorkspaceTab = {
        id: PINNED_SECONDARY_ID,
        type: expectedType,
        title: expectedTitle,
        status: 'idle',
        pinned: true,
        createdAt: new Date().toISOString(),
        data: {}
      };
      const homeIndex = tabs.findIndex(t => t.id === PINNED_HOME_ID);
      tabs.splice(homeIndex + 1, 0, secondaryTab);
      changed = true;
    } else {
      const secondaryTab = tabs.find(t => t.id === PINNED_SECONDARY_ID);
      if (secondaryTab && (secondaryTab.type !== expectedType || secondaryTab.title !== expectedTitle || !secondaryTab.pinned)) {
        secondaryTab.type = expectedType;
        secondaryTab.title = expectedTitle;
        secondaryTab.pinned = true;
        changed = true;
      }
    }

    if (changed) {
      this.tabs$.next(tabs);
      if (!this.activeTabId$.value || !tabs.some(t => t.id === this.activeTabId$.value)) {
        this.activeTabId$.next(PINNED_HOME_ID);
      }
      this.persist();
    }
  }

  transitionOnboardingToProfile(): void {
    const tabs = this.tabs$.value.map(t => {
      if (t.id !== PINNED_SECONDARY_ID || t.type !== 'onboarding') return t;
      return { ...t, type: 'profile' as WorkspaceTabType, title: 'My Profile' };
    });
    this.tabs$.next(tabs);
    this.persist();
  }

  // An empty "New Tab" is a placeholder with nothing in it, so there is nothing to restore. Older
  // sessions have one persisted from when the workspace seeded it on startup; dropping it here
  // clears those without the user having to close it by hand.
  private withoutPlaceholderTabs(tabs: WorkspaceTab[]): WorkspaceTab[] {
    return tabs.filter(tab => {
      if (tab.type !== 'new' || tab.pinned) return true;
      return Object.keys(tab.data || {}).length > 0;
    });
  }

  private generateId(): string {
    return 'tab_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);
  }

  private persist(): void {
    this.persistLocal();
    if (this.firestoreLoaded) {
      this.persistToFirestoreDebounced();
    }
  }

  private persistLocal(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.tabs$.value));
      localStorage.setItem(ACTIVE_TAB_KEY, this.activeTabId$.value);
    } catch (e) {
      console.error('[WorkspaceTabService] Failed to persist locally:', e);
    }
  }

  private persistToFirestoreDebounced(): void {
    if (this.persistTimer) clearTimeout(this.persistTimer);
    this.persistTimer = setTimeout(() => {
      this.customerDB.saveWorkspaceTabs(this.tabs$.value, this.activeTabId$.value);
    }, 1500);
  }

  private loadFromLocalStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed: WorkspaceTab[] = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(t => t && t.id && t.type)) {
          const tabs = this.withoutPlaceholderTabs(parsed);
          this.tabs$.next(tabs);
          if (tabs.length > 0) {
            const savedActive = localStorage.getItem(ACTIVE_TAB_KEY);
            if (savedActive && tabs.some(t => t.id === savedActive)) {
              this.activeTabId$.next(savedActive);
            } else {
              this.activeTabId$.next(tabs[0].id);
            }
          }
          return;
        }
      }
    } catch (e) {
      console.error('[WorkspaceTabService] Failed to load from localStorage:', e);
    }
    // No seed tab here. ensurePinnedTabs() provides Home and the profile tab, and the tab bar's
    // plus button covers creating anything further.
  }

  private loadClosedTabsFromLocalStorage(): void {
    try {
      const raw = localStorage.getItem(CLOSED_TABS_KEY);
      if (raw) {
        const tabs: WorkspaceTab[] = JSON.parse(raw);
        if (Array.isArray(tabs) && tabs.every(t => t && t.id && t.type)) {
          this.closedTabs$.next(tabs);
        }
      }
    } catch (e) {
      console.error('[WorkspaceTabService] Failed to load closed tabs:', e);
    }
  }

  private persistClosedTabs(): void {
    try {
      localStorage.setItem(CLOSED_TABS_KEY, JSON.stringify(this.closedTabs$.value));
    } catch (e) {
      console.error('[WorkspaceTabService] Failed to persist closed tabs:', e);
    }
  }
}
